import React, { createContext, useState, useContext, useRef, ReactNode } from 'react';
import { logToSentry } from 'utils/networkErrorUtils';

export interface AudioContextValue {
    volume: number;
    isPlaying: boolean;
    playAudio: (audioUrl: string, loop?: boolean, defaultVolume?: number, playbackRate?: number) => void;
    stopAudio: () => void;
    audioRef: React.RefObject<HTMLAudioElement | null>;
    currentAudio: HTMLAudioElement | null;
    setCurrentAudio: React.Dispatch<React.SetStateAction<HTMLAudioElement | null>>;
    changeVolume: (newVolume: number) => void;
    togglePlayPause: () => void;
}

const AudioContext = createContext<AudioContextValue | null>(null);

const defaultAudioValue: AudioContextValue = {
    volume: 100,
    isPlaying: false,
    playAudio: () => {},
    stopAudio: () => {},
    audioRef: { current: null },
    currentAudio: null,
    setCurrentAudio: () => {},
    changeVolume: () => {},
    togglePlayPause: () => {}
};

export const useAudio = (): AudioContextValue => {
    const context = useContext(AudioContext);
    return context ?? defaultAudioValue;
};

interface AudioProviderProps {
    children: ReactNode;
}

export const AudioProvider = ({ children }: AudioProviderProps) => {
    const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [volume, setVolume] = useState(100);
    const [isPlaying, setIsPlaying] = useState(false);

    const playAudio = (audioUrl: string, loop = false, defaultVolume = 100, playbackRate = 1) => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        const initialVolume = defaultVolume;
        const audio = new Audio(audioUrl);
        audio.volume = initialVolume / 100;
        audio.loop = loop;
        const rate = Math.max(0.25, Math.min(4, playbackRate));
        audio.playbackRate = rate;
        audioRef.current = audio;
        audio.play();
        setCurrentAudio(audio);
        setVolume(initialVolume);
        setIsPlaying(true);
    };

    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setCurrentAudio(null);
        }
    };

    const changeVolume = (newVolume: number) => {
        const clampedVolume = Math.max(0, Math.min(100, newVolume));
        setVolume(clampedVolume);
        if (audioRef.current) {
            audioRef.current.volume = clampedVolume / 100;
        }
    };

    const pauseAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    };

    const resumeAudio = () => {
        if (audioRef.current) {
            audioRef.current.play().then(() => {
                setIsPlaying(true);
            }).catch(error => {
                logToSentry({
                    message: 'Error resuming audio',
                    error,
                    module: 'AUDIO',
                    operation: 'RESUME_AUDIO'
                })
                console.error('Error resuming audio:', error);
                setIsPlaying(false);
            });
        }
    };

    const togglePlayPause = () => {
        if (audioRef.current) {
            if (isPlaying) {
                pauseAudio();
            } else {
                resumeAudio();
            }
        }
    };

    const value: AudioContextValue = {
        volume,
        isPlaying,
        playAudio,
        stopAudio,
        audioRef,
        currentAudio,
        setCurrentAudio,
        changeVolume,
        togglePlayPause
    };

    return (
        <AudioContext.Provider value={value}>
            {children}
        </AudioContext.Provider>
    );
};
